import { useState, useEffect } from "react";
import { C } from "../lib/colors";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { Logo } from "../components/Logo";
import { PasswordInput } from "../components/PasswordInput";
import { MinimalFooter } from "../components/MinimalFooter";

// Landing page after a new client clicks the invite email link.
// Supabase parses the recovery/invite token from the URL hash automatically
// and signs the user in. Our job is to prompt them to set a password,
// then drop them into their portal.

export const WelcomePage = ({ onNavigate, onClientLogin }) => {
  const [status, setStatus] = useState("loading"); // loading | needsPassword | error | done
  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  // Resend flow (#155, 2026-07-03): when the invite link is expired the user
  // is stuck on the error screen with nowhere to go. This lets them request a
  // fresh link straight from the page. resetPasswordForEmail() works whether
  // or not they've ever set a password — Supabase emails a magic recovery link
  // that lands back on /welcome and lets them (re-)set their password.
  const [resendEmail, setResendEmail]   = useState("");
  const [resendState, setResendState]   = useState("idle"); // idle | sending | sent | error
  const [resendError, setResendError]   = useState("");

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setStatus("error");
      setErrorMessage("Authentication isn't configured on this site.");
      return;
    }

    let cancelled = false;
    const init = async () => {
      // Supabase auto-parses the URL hash on load. Give it a tick to settle.
      await new Promise(r => setTimeout(r, 100));
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;

      if (!session?.user) {
        setStatus("error");
        setErrorMessage(
          "This invite link is no longer valid. It may have expired or already been used. " +
          "Ask your account manager to send a fresh invite."
        );
        return;
      }
      setUser(session.user);
      setStatus("needsPassword");
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const handleSetPassword = async () => {
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErrorMessage("Passwords don't match.");
      return;
    }
    setSaving(true);
    setErrorMessage("");
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw new Error(error.message);

      // Pull the linked clients row so the portal has profile data.
      const { data: row } = await supabase
        .from("clients")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      const clientProfile = row
        ? {
            id:               row.id,
            email:            row.email,
            name:             row.name || user.user_metadata?.name || "Client",
            company:          row.company || "",
            plan:             row.plan || "Growth",
            joinDate:         row.join_date,
            stripeLink:       row.stripe_link,
            stripeCustomerId: row.stripe_customer_id,
          }
        : {
            id:    user.id,
            email: user.email,
            name:  user.user_metadata?.name || "Client",
            plan:  "Growth",
          };

      // Clean the URL (drop the recovery hash) and route into the portal.
      window.history.replaceState({}, "", "/");
      onClientLogin(clientProfile);
    } catch (err) {
      setErrorMessage(err.message || "Could not set password. Please try again.");
      setSaving(false);
    }
  };

  const handleResend = async () => {
    const email = resendEmail.trim().toLowerCase();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setResendError("Enter the email your invite was sent to.");
      return;
    }
    setResendState("sending");
    setResendError("");
    try {
      // resetPasswordForEmail always works if the user exists; if it doesn't,
      // Supabase silently no-ops (doesn't leak account existence). We treat
      // any non-throw as success from the UI perspective.
      const redirectTo = `${window.location.origin}/welcome`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) throw new Error(error.message);
      setResendState("sent");
    } catch (err) {
      setResendState("error");
      setResendError(err.message || "Could not send a new link. Please try again in a moment.");
    }
  };

  return (
    <div style={{minHeight:"100vh",background:"#000000",fontFamily:"'Instrument Sans',sans-serif",color:C.text,
      display:"flex",flexDirection:"column"}}>
      <nav style={{padding:"0 5%",height:96,display:"flex",alignItems:"center",justifyContent:"center",
        borderBottom:`1px solid ${C.border}`}}>
        <Logo size={44}/>
      </nav>

      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{width:"100%",maxWidth:440,animation:"fadeUp 0.4s ease"}}>

          {status === "loading" && (
            <div style={{textAlign:"center",color:C.muted}}>
              <div style={{width:40,height:40,margin:"0 auto 16px",border:`3px solid ${C.border}`,
                borderTopColor:C.green,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              <div style={{fontSize:13}}>Verifying your invite…</div>
            </div>
          )}

          {status === "error" && (
            <div style={{textAlign:"center"}}>
              <div style={{width:64,height:64,margin:"0 auto 18px",borderRadius:"50%",
                background:"rgba(255,82,82,0.12)",border:`2px solid ${C.red}`,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,color:C.red}}>⚠</div>
              <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:24,fontWeight:800,letterSpacing:-1,marginBottom:10}}>
                Invite link unavailable
              </h1>
              <p style={{color:C.muted,fontSize:14,lineHeight:1.65,marginBottom:24}}>
                This invite link is no longer valid. It may have expired or already been used. Enter the email your invite was sent to and we'll send you a fresh link.
              </p>

              {resendState !== "sent" && (
                <div style={{background:C.s1,border:`1px solid ${C.border}`,borderRadius:14,
                  padding:20,marginBottom:16,textAlign:"left"}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:7,color:"rgba(255,255,255,0.7)"}}>
                    Email address
                  </div>
                  <input type="email" value={resendEmail}
                    onChange={e=>setResendEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleResend()}
                    placeholder="you@company.com"
                    autoComplete="email" autoFocus
                    disabled={resendState === "sending"}
                    style={{width:"100%",background:"rgba(255,255,255,0.04)",
                      border:`1px solid ${C.border}`,borderRadius:9,
                      padding:"11px 12px",color:C.text,fontSize:14,
                      fontFamily:"'Instrument Sans',sans-serif",marginBottom:12,
                      boxSizing:"border-box",outline:"none"}}/>
                  {resendError && (
                    <div style={{fontSize:12,color:C.red,marginBottom:12,
                      display:"flex",alignItems:"center",gap:6}}>
                      <span>⚠</span>{resendError}
                    </div>
                  )}
                  <button onClick={handleResend}
                    disabled={resendState === "sending" || !resendEmail}
                    style={{width:"100%",
                      background:(resendState === "sending" || !resendEmail) ? "rgba(255,255,255,0.06)" : C.green,
                      color:(resendState === "sending" || !resendEmail) ? C.muted : "#000",
                      border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:700,
                      cursor:(resendState === "sending" || !resendEmail) ? "not-allowed" : "pointer",
                      fontFamily:"'Instrument Sans',sans-serif",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                    {resendState === "sending"
                      ? <><span style={{width:14,height:14,border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#000",
                          borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Sending…</>
                      : "Send me a fresh link"}
                  </button>
                </div>
              )}

              {resendState === "sent" && (
                <div style={{background:"rgba(0,204,102,0.08)",
                  border:`1px solid ${C.green}`,borderRadius:14,padding:20,marginBottom:16}}>
                  <div style={{fontSize:24,marginBottom:6}}>✓</div>
                  <div style={{fontSize:14,fontWeight:700,color:C.green,marginBottom:6}}>
                    Fresh link sent
                  </div>
                  <div style={{fontSize:13,color:C.muted,lineHeight:1.55}}>
                    Check <strong style={{color:C.text}}>{resendEmail}</strong> for a new access link. It's valid for the next hour. Look in the spam folder if you don't see it in ~2 minutes.
                  </div>
                </div>
              )}

              <button onClick={()=>onNavigate("landing")}
                style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,
                  color:C.muted,padding:"11px 24px",fontSize:13,fontWeight:600,cursor:"pointer",
                  fontFamily:"'Instrument Sans',sans-serif"}}>
                Back to home
              </button>
            </div>
          )}

          {status === "needsPassword" && (
            <>
              <div style={{textAlign:"center",marginBottom:32}}>
                <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
                  width:56,height:56,borderRadius:16,background:`linear-gradient(135deg,${C.green},${C.blue})`,
                  fontSize:24,marginBottom:18}}>👋</div>
                <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:26,fontWeight:800,letterSpacing:-1,marginBottom:8}}>
                  Welcome to Media on Auto Pilot
                </h1>
                <p style={{color:C.muted,fontSize:14,lineHeight:1.6}}>
                  Set a password for <strong style={{color:C.green}}>{user?.email}</strong> and we'll take you to your portal.
                </p>
              </div>

              <div style={{background:C.s1,border:`1px solid ${C.border}`,borderRadius:18,padding:28}}>
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:7,color:"rgba(255,255,255,0.7)"}}>
                    Choose a password
                  </div>
                  <PasswordInput
                    value={password}
                    onChange={e=>setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                  />
                </div>

                <div style={{marginBottom:20}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:7,color:"rgba(255,255,255,0.7)"}}>
                    Confirm password
                  </div>
                  <PasswordInput
                    value={confirm}
                    onChange={e=>setConfirm(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSetPassword()}
                    placeholder="Type it again"
                    autoComplete="new-password"
                  />
                </div>

                {errorMessage && (
                  <div style={{fontSize:12,color:C.red,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
                    <span>⚠</span>{errorMessage}
                  </div>
                )}

                <button onClick={handleSetPassword} disabled={saving||!password||!confirm}
                  style={{width:"100%",background:saving||!password||!confirm?"rgba(255,255,255,0.06)":C.green,
                    color:saving||!password||!confirm?C.muted:"#000",border:"none",borderRadius:10,
                    padding:"13px",fontSize:14,fontWeight:700,
                    cursor:saving||!password||!confirm?"not-allowed":"pointer",
                    fontFamily:"'Instrument Sans',sans-serif",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {saving
                    ? <><span style={{width:14,height:14,border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#000",
                        borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Saving…</>
                    : "Save & Continue →"}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
      <MinimalFooter onNavigate={onNavigate}/>
    </div>
  );
};
